import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, Check, FileText, FilePlus2, Copy, Trash2, Upload, Pencil } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useFeatures, isFeatureVisible } from "@/lib/features";
import { DEFAULT_CV, normalizeImported } from "@/lib/cvUtils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import CvEditor from "@/components/cv/CvEditor";
import JobDescriptionPanel from "@/components/cv/JobDescriptionPanel";
import CvPreview from "@/components/cv/CvPreview";

export default function CvCreator() {
  useFeatures();
  const [list, setList] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [cv, setCv] = useState(null);
  const [name, setName] = useState("My CV");
  const [renaming, setRenaming] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const loaded = useRef(false);
  const saveTimer = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [l, t] = await Promise.all([api.cvsList(), api.cvTemplates().catch(() => ({ templates: [] }))]);
        setTemplates(t.templates || []);
        const cvs = l.cvs || [];
        setList(cvs);
        if (cvs.length) await openCv(cvs[0].id);
        else setCv(DEFAULT_CV());
      } catch { setCv(DEFAULT_CV()); }
      finally { setTimeout(() => (loaded.current = true), 350); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCv = async (id) => {
    loaded.current = false;
    const g = await api.cvsGet(id);
    setCurrentId(g.id); setName(g.name); setSaved(g.updated_at);
    setCv({ ...DEFAULT_CV(), ...(g.data || {}) });
    setTimeout(() => (loaded.current = true), 350);
  };

  // autosave (debounced)
  useEffect(() => {
    if (!cv || !currentId || !loaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try { const r = await api.cvsUpdate(currentId, { data: cv }); setSaved(r.updated_at); } catch { /* keep editing */ } finally { setSaving(false); }
    }, 900);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [cv, currentId]);

  const setLayout = useMemo(() => (sp, fs) => {
    setCv((c) => { if (!c) return c; const ls = c.layout_settings || {}; if (ls.sp === sp && ls.fs === fs) return c; return { ...c, layout_settings: { sp, fs } }; });
  }, []);

  const createCv = async () => {
    try { const r = await api.cvsCreate("Untitled CV"); setList((l) => [{ id: r.id, name: r.name, updated_at: r.updated_at }, ...l]); await openCv(r.id); toast.success("New CV created"); }
    catch { toast.error("Couldn't create CV."); }
  };
  const duplicateCv = async () => {
    if (!currentId) return;
    try { const r = await api.cvsDuplicate(currentId); setList((l) => [{ id: r.id, name: r.name, updated_at: r.updated_at }, ...l]); await openCv(r.id); toast.success("Duplicated"); }
    catch { toast.error("Couldn't duplicate."); }
  };
  const deleteCv = async () => {
    if (!currentId || list.length <= 1) return toast.message("Keep at least one CV.");
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
    try {
      await api.cvsDelete(currentId);
      const rest = list.filter((x) => x.id !== currentId);
      setList(rest); await openCv(rest[0].id); toast.success("Deleted");
    } catch { toast.error("Couldn't delete."); }
  };
  const saveName = async () => {
    setRenaming(false);
    try { await api.cvsUpdate(currentId, { name }); setList((l) => l.map((x) => x.id === currentId ? { ...x, name } : x)); }
    catch { toast.error("Couldn't rename."); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const r = await api.cvImport(file);
      const normalized = normalizeImported(r.data);
      setCv((c) => ({ ...normalized, template: c?.template || "modern", layout_settings: c?.layout_settings || { sp: 1, fs: 1 } }));
      toast.success("Résumé imported — review and edit every field");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't import that file."); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!isFeatureVisible("cv_creator")) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white"><Navbar />
        <div className="mx-auto max-w-lg px-6 py-24 text-center" data-testid="cv-unavailable">
          <FileText className="mx-auto mb-4 text-white/30" size={40} />
          <h1 className="text-xl font-bold">CV Creator is currently unavailable</h1>
          <Link to="/tools" className="mt-6 inline-block rounded-full bg-white px-4 py-2 text-sm font-bold text-black">Back to Tools</Link>
        </div>
      </div>
    );
  }
  if (!cv) return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="animate-spin text-emerald-400" /></div>;

  const iconBtn = "inline-flex items-center gap-1 rounded-md border border-white/15 px-2.5 py-1.5 text-[12px] font-semibold text-white hover:border-white/30";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="mx-auto max-w-[1600px] px-4 py-4 sm:px-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2.5">
            <Link to="/tools" data-testid="cv-back" className="flex items-center gap-1 text-sm text-white/60 hover:text-white"><ArrowLeft size={16} /> Tools</Link>
            {renaming ? (
              <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onBlur={saveName} onKeyDown={(e) => e.key === "Enter" && saveName()} data-testid="cv-name-input" className="h-8 w-48 border-white/15 bg-black/40 text-sm text-white" />
            ) : (
              <button onClick={() => setRenaming(true)} data-testid="cv-name" className="flex items-center gap-1.5 font-display text-lg font-black tracking-tight hover:text-emerald-300">{name} <Pencil size={13} className="text-white/40" /></button>
            )}
            {list.length > 1 && (
              <Select value={currentId || ""} onValueChange={openCv}>
                <SelectTrigger className="h-8 w-44 border-white/10 bg-white/5 text-[12px] text-white" data-testid="cv-switcher"><SelectValue placeholder="Switch CV" /></SelectTrigger>
                <SelectContent className="border-white/10 bg-[#111214] text-white">
                  {list.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`cv-switch-${c.id}`}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="cv-import-btn" className="inline-flex items-center gap-1 rounded-md border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-1.5 text-[12px] font-semibold text-emerald-300 hover:bg-emerald-400/20 disabled:opacity-50">{importing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Import résumé</button>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => doImport(e.target.files?.[0])} data-testid="cv-import-input" />
            <button onClick={createCv} data-testid="cv-new-btn" className={iconBtn}><FilePlus2 size={13} /> New</button>
            <button onClick={duplicateCv} data-testid="cv-dup-btn" className={iconBtn}><Copy size={13} /> Duplicate</button>
            <button onClick={deleteCv} data-testid="cv-del-btn" className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2.5 py-1.5 text-[12px] font-semibold text-white/70 hover:border-red-400/50 hover:text-red-400"><Trash2 size={13} /> Delete</button>
            <span className="ml-1 text-[12px] text-white/50" data-testid="cv-save-status">
              {saving ? <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Saving…</span>
                : saved ? <span className="inline-flex items-center gap-1 text-emerald-300"><Check size={13} /> Saved</span> : "Draft"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="order-2 lg:order-1">
            <Tabs defaultValue="build">
              <TabsList className="mb-3 bg-white/5" data-testid="cv-tabs">
                <TabsTrigger value="build" data-testid="cv-tab-build" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black">Build CV</TabsTrigger>
                <TabsTrigger value="tailor" data-testid="cv-tab-tailor" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black">Tailor to Job</TabsTrigger>
              </TabsList>
              <TabsContent value="build"><CvEditor cv={cv} onChange={setCv} /></TabsContent>
              <TabsContent value="tailor"><JobDescriptionPanel cv={cv} onChange={setCv} /></TabsContent>
            </Tabs>
          </div>
          <div className="order-1 lg:order-2">
            <div className="lg:sticky lg:top-[72px]" style={{ height: "calc(100vh - 96px)" }}>
              <CvPreview cv={cv} templates={templates} onLayout={setLayout} onTemplate={(t) => setCv((c) => ({ ...c, template: t }))} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
